import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { TuneOutlined } from '@material-ui/icons';
import { useDataProvider, useNotify } from 'react-admin';
import { CardTitle } from 'components/common';
import { ShowLayout } from 'components/details';
import formatPrice from 'services/helpers/formatPrice';

const useStyles = makeStyles((theme) => ({
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    minWidth: 680,
  },
  tableHeaderCell: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  tableCell: {
    verticalAlign: 'top',
    wordBreak: 'break-word',
  },
  emptyState: {
    color: theme.palette.text.secondary,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const AdjustmentsSection = (props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const shipmentId = props?.record?.id;

  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!shipmentId) {
      setAdjustments([]);
      return;
    }

    let active = true;

    const loadAdjustments = async () => {
      try {
        setLoading(true);
        const { data } = await dataProvider.getNest('shipmentAdjustments', { id: shipmentId });

        if (!active) {
          return;
        }

        setAdjustments(Array.isArray(data) ? data : []);
      } catch (error: any) {
        if (!active) {
          return;
        }

        setAdjustments([]);
        notify(
          typeof error === 'string' ? error : error?.message || 'ra.notification.http_error',
          'warning'
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadAdjustments();

    return () => {
      active = false;
    };
  }, [dataProvider, notify, shipmentId]);

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (!adjustments.length) {
    return (
      <Typography className={classes.emptyState} variant="body2">
        No adjustments found
      </Typography>
    );
  }

  return (
    <div className={classes.tableWrapper}>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeaderCell}>Source</TableCell>
            <TableCell className={classes.tableHeaderCell}>Created at</TableCell>
            <TableCell className={classes.tableHeaderCell}>Amount</TableCell>
            <TableCell className={classes.tableHeaderCell}>Description</TableCell>
            <TableCell className={classes.tableHeaderCell}>Service type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {adjustments.map((adjustment, index) => (
            <TableRow key={adjustment?.id || index}>
              <TableCell className={classes.tableCell}>{adjustment?.source || '-'}</TableCell>
              <TableCell className={classes.tableCell}>
                {formatDateTime(adjustment?.createdAt)}
              </TableCell>
              <TableCell className={classes.tableCell}>{formatPrice(adjustment?.amount)}</TableCell>
              <TableCell className={classes.tableCell}>{adjustment?.description || '-'}</TableCell>
              <TableCell className={classes.tableCell}>{adjustment?.serviceType || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const AdjustmentsCard = () => (
  <ShowLayout>
    <CardTitle icon={<TuneOutlined />}>Adjustments</CardTitle>
    <AdjustmentsSection />
  </ShowLayout>
);

export default AdjustmentsCard;
